/**
 * In Patient Initial Medical Assessment Form Configuration
 * Section-wise, index-based form field definitions
 */
import { calculateAge } from '../../utils/functions';


export const formSections = [
    {
        sectionIndex: 1,
        sectionTitle: 'Patient Basic Details',
        hideInUI: true,
        fields: [
            {
                fieldIndex: 1,
                fieldKey: 'patientName',
                sourceKey: 'name',
                label: 'Patient Name',
                componentType: 'text',
                placeholder: 'Enter patient name',
                required: true,
                gridClass: 'col-xl-4 col-md-6',
            },
            {
                fieldIndex: 2,
                fieldKey: 'casePaperNo',
                sourceKey: 'registrationNo',
                label: 'Case Paper No',
                componentType: 'text',
                placeholder: 'Enter case paper number',
                gridClass: 'col-xl-4 col-md-6',
            },
            {
                fieldIndex: 3,
                fieldKey: 'age',
                label: 'Age (Auto-calculated)',
                componentType: 'number',
                placeholder: 'Auto-calculated from Date of Birth',
                required: true,
                disabled: true,
                gridClass: 'col-xl-4 col-md-6',
            },
            {
                fieldIndex: 4,
                fieldKey: 'gender',
                sourceKey: 'gender',
                label: 'Gender',
                componentType: 'singleSelect',
                options: [
                    { label: 'Male', value: 'Male' },
                    { label: 'Female', value: 'Female' },
                    { label: 'Other', value: 'Other' },
                ],
                required: true,
                gridClass: 'col-xl-4 col-md-6',
            },
            {
                fieldIndex: 5,
                fieldKey: 'dateOfBirth',
                sourceKey: 'birthDate',
                label: 'Date of Birth',
                componentType: 'date',
                placeholder: 'Select date of birth',
                required: true,
                gridClass: 'col-xl-4 col-md-6',
            },
            {
                fieldIndex: 6,
                fieldKey: 'roomName',
                label: 'Room Name',
                componentType: 'text',
                gridClass: 'col-xl-4 col-md-6',
            },
            {
                fieldIndex: 7,
                fieldKey: 'roomType',
                label: 'Room Type',
                componentType: 'text',
                gridClass: 'col-xl-4 col-md-6',
            },
            {
                fieldIndex: 8,
                fieldKey: 'consultantName',
                label: 'Consultant Name',
                componentType: 'text',
                placeholder: 'Enter consultant name',
                gridClass: 'col-xl-4 col-md-6',
            },
        ],
    },

    {
        sectionIndex: 2,
        sectionTitle: 'Chief Complaint',
        fields: [
            {
                fieldIndex: 1,
                fieldKey: 'consultantName',
                label: 'Consultant Name',
                componentType: 'text',
                placeholder: 'Enter consultant name',
                gridClass: 'col-xl-4 col-md-6',
            },
            {
                fieldIndex: 2,
                fieldKey: 'chiefComplaint',
                label: 'Chief Complaint',
                componentType: 'textarea',
                placeholder: 'Enter chief complaint',
                required: true,
                rows: 3,
                gridClass: 'col-12',
            },
        ],
    },

    {
        sectionIndex: 3,
        sectionTitle: 'Present & Past Medical History',
        fields: [
            {
                fieldIndex: 1,
                fieldKey: 'medicalHistory',
                label: 'Medical History (Past & Present)',
                componentType: 'checkbox',
                options: [
                    { label: 'Diabetes Mellitus (DM)', value: 'diabetes' },
                    { label: 'Hypertension (HTN)', value: 'hypertension' },
                    { label: 'Coronary Artery Disease (CAD)', value: 'cad' },
                    { label: 'Renal Disease', value: 'renalDisease' },
                    { label: 'Hyperthyroidism', value: 'hyperthyroidism' },
                    { label: 'Hypothyroidism', value: 'hypothyroidism' },
                ],
                gridClass: 'col-12',
            },
            {
                fieldIndex: 2,
                fieldKey: 'diabetesDuration',
                label: 'Diabetes Duration',
                componentType: 'text',
                placeholder: 'e.g. 5 years',
                gridClass: 'col-xl-6 col-md-12',
                conditionalLogic: {
                    showIf: 'medicalHistory',
                    condition: (value) =>
                        Array.isArray(value) && value.includes('diabetes'),
                },
            },
            {
                fieldIndex: 3,
                fieldKey: 'hypertensionDuration',
                label: 'Hypertension Duration',
                componentType: 'text',
                placeholder: 'e.g. 5 years',
                gridClass: 'col-xl-6 col-md-12',
                conditionalLogic: {
                    showIf: 'medicalHistory',
                    condition: (value) =>
                        Array.isArray(value) && value.includes('hypertension'),
                },
            },
            {
                fieldIndex: 4,
                fieldKey: 'cadDuration',
                label: 'CAD Duration',
                componentType: 'text',
                placeholder: 'e.g. 5 years',
                gridClass: 'col-xl-6 col-md-12',
                conditionalLogic: {
                    showIf: 'medicalHistory',
                    condition: (value) =>
                        Array.isArray(value) && value.includes('cad'),
                },
            },
            {
                fieldIndex: 5,
                fieldKey: 'renalDiseaseDuration',
                label: 'Renal Disease Duration',
                componentType: 'text',
                placeholder: 'e.g. 5 years',
                gridClass: 'col-xl-6 col-md-12',
                conditionalLogic: {
                    showIf: 'medicalHistory',
                    condition: (value) =>
                        Array.isArray(value) && value.includes('renalDisease'),
                },
            },
            {
                fieldIndex: 6,
                fieldKey: 'hyperthyroidismDuration',
                label: 'Hyperthyroidism Duration',
                componentType: 'text',
                placeholder: 'e.g. 5 years',
                gridClass: 'col-xl-6 col-md-12',
                conditionalLogic: {
                    showIf: 'medicalHistory',
                    condition: (value) =>
                        Array.isArray(value) && value.includes('hyperthyroidism'),
                },
            },
            {
                fieldIndex: 7,
                fieldKey: 'hypothyroidismDuration',
                label: 'Hypothyroidism Duration',
                componentType: 'text',
                placeholder: 'e.g. 5 years',
                gridClass: 'col-xl-6 col-md-12',
                conditionalLogic: {
                    showIf: 'medicalHistory',
                    condition: (value) =>
                        Array.isArray(value) && value.includes('hypothyroidism'),
                },
            },
        ],
    },

    {
        sectionIndex: 4,
        sectionTitle: 'Nutrition',
        fields: [
            {
                fieldIndex: 1,
                fieldKey: 'nutrition',
                label: 'Nutritional Status',
                componentType: 'checkbox',
                options: [
                    { label: 'Weight Loss', value: 'weightLoss' },
                    { label: 'Low Food Intake', value: 'lowFoodIntake' },
                    { label: 'Obese', value: 'obese' },
                    { label: 'Adequate Nutrition', value: 'adequateNutrition' },
                ],
                gridClass: 'col-12',
            },
        ],
    },

    {
        sectionIndex: 5,
        sectionTitle: 'Personal History',
        fields: [
            {
                fieldIndex: 1,
                fieldKey: 'bowel',
                label: 'Bowel',
                componentType: 'singleSelect',
                options: [
                    { label: 'Altered', value: 'altered' },
                    { label: 'Unaltered', value: 'unaltered' },
                ],
                gridClass: 'col-xl-6 col-md-12',
            },
            {
                fieldIndex: 2,
                fieldKey: 'bladder',
                label: 'Bladder',
                componentType: 'singleSelect',
                options: [
                    { label: 'Altered', value: 'altered' },
                    { label: 'Unaltered', value: 'unaltered' },
                ],
                gridClass: 'col-xl-6 col-md-12',
            },
            {
                fieldIndex: 3,
                fieldKey: 'waterIntake',
                label: 'Water Intake',
                componentType: 'singleSelect',
                options: [
                    { label: 'Adequate', value: 'adequate' },
                    { label: 'Inadequate', value: 'inadequate' },
                ],
                gridClass: 'col-xl-6 col-md-12',
            },
            {
                fieldIndex: 4,
                fieldKey: 'sleep',
                label: 'Sleep',
                componentType: 'singleSelect',
                options: [
                    { label: 'Normal', value: 'normal' },
                    { label: 'Disturbed', value: 'disturbed' },
                ],
                gridClass: 'col-xl-6 col-md-12',
            },
        ],
    },

    {
        sectionIndex: 6,
        sectionTitle: 'Addictions',
        fields: [
            {
                fieldIndex: 1,
                fieldKey: 'addictions',
                label: 'Addictions',
                componentType: 'checkbox',
                options: [
                    { label: 'Smoking / Tobacco', value: 'smokingTobacco' },
                    { label: 'Alcohol', value: 'alcohol' },
                ],
                gridClass: 'col-12',
            },
            {
                fieldIndex: 2,
                fieldKey: 'smokingDuration',
                label: 'Smoking / Tobacco Duration',
                componentType: 'text',
                placeholder: 'e.g. 10 years',
                gridClass: 'col-xl-6 col-md-12',
                conditionalLogic: {
                    showIf: 'addictions',
                    condition: (value) =>
                        Array.isArray(value) && value.includes('smokingTobacco'),
                },
            },
            {
                fieldIndex: 3,
                fieldKey: 'alcoholDuration',
                label: 'Alcohol Duration',
                componentType: 'text',
                placeholder: 'e.g. 5 years',
                gridClass: 'col-xl-6 col-md-12',
                conditionalLogic: {
                    showIf: 'addictions',
                    condition: (value) =>
                        Array.isArray(value) && value.includes('alcohol'),
                },
            },
        ],
    },

    {
        sectionIndex: 7,
        sectionTitle: 'History of Allergies',
        fields: [
            {
                fieldIndex: 1,
                fieldKey: 'allergyHistory',
                label: 'Allergy History',
                componentType: 'textarea',
                placeholder: 'Enter allergy details if any',
                rows: 2,
                gridClass: 'col-xl-6 col-md-12',
            },
        ],
    },

    {
        sectionIndex: 8,
        sectionTitle: 'Local Examination',
        fields: [
            {
                fieldIndex: 1,
                fieldKey: 'localExaminationNotes',
                label: 'Local Examination Notes',
                componentType: 'textarea',
                placeholder: 'Enter examination findings',
                rows: 2,
                gridClass: 'col-xl-6 col-md-12',
            },
        ],
    },

    {
        sectionIndex: 9,
        sectionTitle: 'Pain Screening',
        fields: [
            {
                fieldIndex: 1,
                fieldKey: 'painScore',
                label: 'Pain Score (0 = No Pain, 10 = Worst Pain)',
                componentType: 'painRating',
                gridClass: 'col-12',
            },
        ],
    },

    {
        sectionIndex: 10,
        sectionTitle: 'General Examination',
        fields: [
            {
                fieldIndex: 1,
                fieldKey: 'generalExamination',
                label: 'General Examination',
                componentType: 'checkbox',
                options: [
                    { label: 'Pallor', value: 'pallor' },
                    { label: 'Icterus', value: 'icterus' },
                    { label: 'Clubbing', value: 'clubbing' },
                    { label: 'Edema', value: 'edema' },
                    { label: 'Cyanosis', value: 'cyanosis' },
                    { label: 'Raised JVP', value: 'jvp' },
                    { label: 'Lymphadenopathy', value: 'lymphNodes' },
                ],
                gridClass: 'col-12',
            },
        ],
    },

    {
        sectionIndex: 11,
        sectionTitle: 'Vitals & Systemic Examination',
        fields: [
            {
                fieldIndex: 1,
                fieldKey: 'pulse',
                label: 'Pulse Rate (bpm)',
                placeholder: 'e.g. 72',
                componentType: 'number',
                gridClass: 'col-xl-3 col-md-6',
            },
            {
                fieldIndex: 2,
                fieldKey: 'bp',
                label: 'Blood Pressure (mmHg)',
                placeholder: 'e.g. 120/80',
                componentType: 'text',
                required: true,
                gridClass: 'col-xl-3 col-md-6',
            },
            {
                fieldIndex: 3,
                fieldKey: 'rr',
                label: 'Respiratory Rate (breaths/min)',
                placeholder: 'e.g. 16',
                componentType: 'number',
                required: true,
                gridClass: 'col-xl-3 col-md-6',
            },
            {
                fieldIndex: 5,
                fieldKey: 'temperature',
                label: 'Body Temperature (°F)',
                placeholder: 'e.g. 98.6',
                componentType: 'number',
                step: '0.1',
                gridClass: 'col-xl-3 col-md-6',
            },
            {
                fieldIndex: 6,
                fieldKey: 'spo2',
                label: 'SpO₂ (%)',
                placeholder: 'e.g. 98',
                componentType: 'number',
                gridClass: 'col-xl-3 col-md-6',
            },
            {
                fieldIndex: 7,
                fieldKey: 'chestExamination',
                label: 'Chest Examination (Respiratory System)',
                componentType: 'textarea',
                rows: 2,
                gridClass: 'col-xl-6 col-md-12',
            },
            {
                fieldIndex: 8,
                fieldKey: 'cvs',
                label: 'Cardiovascular System (CVS)',
                componentType: 'textarea',
                rows: 2,
                gridClass: 'col-xl-6 col-md-12',
            },
            {
                fieldIndex: 9,
                fieldKey: 'abdomen',
                label: 'Abdominal Examination',
                componentType: 'textarea',
                rows: 2,
                gridClass: 'col-xl-6 col-md-12',
            },
            {
                fieldIndex: 10,
                fieldKey: 'cns',
                label: 'Central Nervous System (CNS)',
                componentType: 'textarea',
                rows: 2,
                gridClass: 'col-xl-6 col-md-12',
            },
        ],
    },

    {
        sectionIndex: 12,
        sectionTitle: 'Local Investigation',
        fields: [
            {
                fieldIndex: 1,
                fieldKey: 'localInvestigation',
                label: 'Local Investigation',
                componentType: 'textarea',
                rows: 2,
                gridClass: 'col-xl-6 col-md-12',
            },
        ],
    },

    {
        sectionIndex: 13,
        sectionTitle: 'Diagnosis',
        fields: [
            {
                fieldIndex: 1,
                fieldKey: 'diagnosis',
                label: 'Diagnosis',
                componentType: 'textarea',
                required: true,
                rows: 2,
                gridClass: 'col-xl-6 col-md-12',
            },
        ],
    },

    {
        sectionIndex: 14,
        sectionTitle: 'Family History',
        fields: [
            {
                fieldIndex: 1,
                fieldKey: 'familyHistory',
                label: 'Family History',
                componentType: 'textarea',
                rows: 2,
                gridClass: 'col-xl-6 col-md-12',
            },
        ],
    },

    {
        sectionIndex: 15,
        sectionTitle: 'Plan of Care',
        fields: [
            {
                fieldIndex: 1,
                fieldKey: 'planOfCare',
                label: 'Plan of Care',
                componentType: 'textarea',
                rows: 2,
                gridClass: 'col-xl-6 col-md-12',
            },
        ],
    },

    {
        sectionIndex: 16,
        sectionTitle: 'Current Allopathic Medications',
        fields: [
            {
                fieldIndex: 1,
                fieldKey: 'currentMedications',
                label: 'Current Medications',
                componentType: 'textarea',
                rows: 2,
                gridClass: 'col-xl-6 col-md-12',
            },
        ],
    },

    {
        sectionIndex: 17,
        sectionTitle: 'Doctor Authentication',
        fields: [
            {
                fieldIndex: 1,
                fieldKey: 'doctorName',
                label: 'Doctor Name',
                componentType: 'text',
                required: true,
                gridClass: 'col-xl-4 col-md-6',
            },
            {
                fieldIndex: 2,
                fieldKey: 'authDate',
                label: 'Date',
                componentType: 'date',
                autoFill: 'currentDate',
                gridClass: 'col-xl-2 col-md-6',
            },
            {
                fieldIndex: 3,
                fieldKey: 'authTime',
                label: 'Time',
                componentType: 'text',
                autoFill: 'currentTime',
                gridClass: 'col-xl-2 col-md-6',
            },
        ],
    },
];



/**
 * Generate initial values from form configuration
 */
export const generateInitialValues = (patientDetail = null) => {
    const initialValues = {};

    formSections.forEach((section) => {
        section.fields.forEach((field) => {
            // 1. Set default base values
            switch (field.componentType) {
                case 'checkbox':
                    initialValues[field.fieldKey] = [];
                    break;
                case 'number':
                case 'painRating':
                    initialValues[field.fieldKey] = 0;
                    break;
                case 'date':
                    if (field.autoFill === 'currentDate') {
                        initialValues[field.fieldKey] = new Date().toISOString().split('T')[0];
                    } else {
                        initialValues[field.fieldKey] = '';
                    }
                    break;
                case 'text':
                    if (field.autoFill === 'currentTime') {
                        const now = new Date();
                        initialValues[field.fieldKey] = now.toLocaleTimeString('en-US', {
                            hour: '2-digit',
                            minute: '2-digit'
                        });
                    } else {
                        initialValues[field.fieldKey] = '';
                    }
                    break;
                default:
                    initialValues[field.fieldKey] = '';
            }

            // 2. Override with patientDetail data if mapping exists
            if (patientDetail && field.sourceKey && patientDetail[field.sourceKey]) {
                let value = patientDetail[field.sourceKey];

                // Special handling for birthDate to format for input type="date"
                if (field.sourceKey === 'birthDate' && typeof value === 'string') {
                    value = value.split('T')[0];
                }

                initialValues[field.fieldKey] = value;
            }
        });
    });

    // 3. Special Case: Auto-calculate Age if birthDate is available
    if (patientDetail?.birthDate) {
        initialValues.age = calculateAge(patientDetail.birthDate);
    } else if (patientDetail?.age) {
        initialValues.age = patientDetail.age;
    }

    return initialValues;
};
